import { Button, FormControl, FormControlLabel, FormLabel, Grid, Paper, Radio, RadioGroup, Switch } from "@material-ui/core";
import CustomInput from "components/CustomInput";
import { useGetAllActivityItemsQuery, useGetAllActivityTypesQuery } from "features/activity/activitySlice";
import { useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import { ruleFieldType, ruleFieldTypeConfig } from "../../consts";
import { useAddMechanicalRuleMutation, useDeleteMechanicalsConfigMutation, useGetMechanicalRuleTypesQuery, useUpdateMechanicalRuleMutation } from "../../mechanicalsSlice";
import { lotMechanicalConfigStyles } from "./style";
import { useState } from "react";
import { useEffect } from "react";
import NotificationProvider from "components/NotificationProvider";
import { IconDetailing, IconFuel, IconInspect, IconMechanic, IconOil, IconTire } from "icons";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import Authorize from "components/Authorize";
import usePrompt from "hooks/usePrompt";
import { useGetServiceProvidersQuery } from "features/serviceProvider/serviceProviderSlice";
import DropdownFilter from "components/DropdownFilter";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const Rule = ({ rule, onCancel, selectLot, clientLots }) => {
    const classes = lotMechanicalConfigStyles();

    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: vehicleTypes, error: vehicleTypesError, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();//TODO only mechanicals
    let { data: mechanicalRuleTypes, error: mechanicalRuleTypesError, isFetching: isLoadingMechanicalRuleTypes } = useGetMechanicalRuleTypesQuery();//TODO only mechanicals
    let { data: serviceProviders, error: serviceProvidersError, isLoading: isLoadingServiceProviders } = useGetServiceProvidersQuery();
    let [updateMechanicalRule, { isLoading: isSaving }] = useUpdateMechanicalRuleMutation();
    let [deleteRule, { isLoading: isDeleting }] = useDeleteMechanicalsConfigMutation();
    let [addRule, { isLoading: isAdding }] = useAddMechanicalRuleMutation();
    clientLots = clientLots?.map(c => ({ id: c.lotId, name: c.name }));;
    
    const { triggerPrompt } = usePrompt();
    const [showLotDropdown, setShowLotDropdown] = useState(false);

    const ruleOperands = [
        {
            id: 1,
            name: 'And',
        },
        {
            id: 2,
            name: 'Or',
        }
    ]

    const ruleIcons = {
        'oil': <IconOil />,
        'fuel': <IconFuel />,
        'tire': <IconTire />,
        'inspection': <IconInspect />,
        'detailing': <IconDetailing />,
        DEFAULT: <IconMechanic />
    }
    
    const getIcon = (activityId) => {
        const parsedName = activityItems?.find(a => a.id == activityId)?.name.split(' ')[0].toLowerCase();
        const icon = ruleIcons[parsedName];
        return icon ?? ruleIcons.DEFAULT
    }

    const [ruleToEdit, setRuleToEdit] = useState({ ...rule });

    useEffect(() => {
        setRuleToEdit(rule);
    }, [rule]);

    const onFieldChange = (field, val) => {
        setRuleToEdit({ ...ruleToEdit, [field]: val })
    }

    const onRuleParamChange = (val, type, subType) => {
        let params = [...ruleToEdit?.ruleParams]
        let index = params.findIndex(p => p.paramType === type && p?.paramSubType === subType)
        if (index === -1) {
            params = [...params, { ['paramType']: type, ['paramSubType']: subType, ['paramValue']: val, ['ruleId']: ruleToEdit?.id }]
        }
        else {
            params[index] = { ...params[index], ['paramValue']: val };
        }
        setRuleToEdit({ ...ruleToEdit, ['ruleParams']: params })
    }

    const onChangeVehicleTypes = (val) => {
        if(val[val.length - 1] == null && val.length !== 0) {
            val = [null];
            onFieldChange("vehicleTypeIdsList", val);
        }
        else{
            if(ruleToEdit.vehicleTypeIdsList?.includes(null)) { 
                val = val.filter(v => v != null);
                onFieldChange("vehicleTypeIdsList", val);
            } else { 
                onFieldChange("vehicleTypeIdsList", val);
            }
        }
    };

    const onSelectLots = (val) => {
        onFieldChange("lotId", val)
    }

    const onSave = async () => {
        let res;
        if (ruleToEdit?.isNew) {            
            res = await addRule({
                clientAccountId: ruleToEdit.clientAccountId,
                lotIds: ruleToEdit?.appliesTo == "specific-lots" || !ruleToEdit?.appliesTo ? (!Array.isArray(ruleToEdit.lotId) ? [ruleToEdit.lotId] : ruleToEdit.lotId) : null,
                activityId: ruleToEdit.activityId,
                vehicleTypeIdsList: ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList,
                calcOnCheckin: ruleToEdit?.calcOnCheckin || false,
                ruleOperand: ruleToEdit?.ruleOperand || 1,
                daysToComplete: ruleToEdit?.daysToComplete,
                preferredServiceProviderId: ruleToEdit?.preferredServiceProviderId,
                ruleParams: ruleToEdit?.ruleParams?.filter(r => r?.paramValue) || [],
                allLots: ruleToEdit?.appliesTo == "all-lots" ? true : false,
                allPmfLots: ruleToEdit?.appliesTo == "all-pmf-lots" ? true : false
            })
        }
        else {
            var vehicleTypeIdsList = ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList;
            setRuleToEdit({...ruleToEdit, ['vehicleTypeIdsList']:vehicleTypeIdsList})
            res = await updateMechanicalRule(ruleToEdit);
        }
        if (!res?.error) {
            NotificationProvider.success('Successfully saved rule');
            onCancel();
        } else {
            NotificationProvider.error('Failed to save rule');
            setRuleToEdit({ ...rule });
        }
    }

    const onDelete = async () => {
        triggerPrompt({
            title: "Delete Rule",
            content: "Are you sure you want to delete the rule?",
            onConfirm: async () => {
                const res = await deleteRule({
                    id: ruleToEdit?.id
                })

                if (!res?.error) {
                    NotificationProvider.success('Successfully deleted rule');
                } else {
                    NotificationProvider.error('Failed to delete rule');
                }
            },
        });
    }

    const cancel = () => {
        if (ruleToEdit?.isNew) {
            onCancel();
        }
        else {
            setRuleToEdit(rule)
        }
    }

    const allRuleConfigs = mechanicalRuleTypes?.filter(c => c.paramType != ruleFieldType.VEHICLE_TYPE);
    const vehicleTypeConfig = mechanicalRuleTypes?.filter(c => c.paramType === ruleFieldType.VEHICLE_TYPE);

    const allRuleConfigsLookup = allRuleConfigs?.reduce(function (map, obj) {
        if (map[obj.activityId]) {
            map[obj.activityId] = [...map[obj.activityId], obj]
        } else {
            map[obj.activityId] = [obj];
        }
        return map;
    }, {})

    const vehicleTypeConfigLookup = vehicleTypeConfig?.reduce(function (map, obj) {
        map[obj.activityId] = obj;
        return map;
    }, {})

    const onActivityChange = (val) => {
        setRuleToEdit({
            ...ruleToEdit,
            activityId: val ? parseInt(val) : val,
            vehicleTypeIdsList: null,
            calcOnCheckin: false,
            ruleOperand: 1,
            daysToComplete: null,
            preferredServiceProviderId: null,
            ruleParams: allRuleConfigsLookup?.[parseInt(val)] || []
        })
    }

    const handleRadioChange = (event) => {
        onFieldChange("appliesTo", event.target.value)
        if(event.target.value == "specific-lots") { setShowLotDropdown(true) }
        else { setShowLotDropdown(false) }
    };

    const filteredActivityItems = activityItems?.filter(a => mechanicalRuleTypes?.find(m => m.activityId === a.id))

    return (
        <Paper className={`${classes.listItemWrapper} ${ruleToEdit?.isNew ? classes.listItemWrapperNew : ""}`}>
            <Grid className={classes.ruleWrapper} container direction='column' spacing={1}>
                <div className={classes.iconsWrapper}>
                    <div className={classes.iconWrapper}>{getIcon(ruleToEdit.activityId)}</div>
                </div>
                <Grid item className={classes.titleWrapper}>                    
                    {!ruleToEdit?.isNew ?
                        <div className={classes.title}>
                            {activityItems?.find(a => a.id == ruleToEdit.activityId)?.name}
                        </div>
                        :
                        <Grid container direction='column'>
                            {selectLot && 
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    <Grid item>
                                        <FormControl>
                                            <FormLabel id="lot-radio-group">Select an option for the rule to apply to</FormLabel>
                                            <RadioGroup
                                                value={ruleToEdit?.appliesTo}
                                                onChange={handleRadioChange}
                                                aria-labelledby="lot-radio-group"
                                                name="lot-radio-group"
                                                className={classes.lotContainer}
                                                row
                                            >
                                                <FormControlLabel value="specific-lots" control={<Radio />} label="Specific Lots" />
                                                <FormControlLabel value="all-lots" control={<Radio />} label="All Lots" />
                                                <FormControlLabel value="all-pmf-lots" control={<Radio />} label="All PMF Lots" />
                                            </RadioGroup>                                    
                                        </FormControl>
                                    </Grid>
                                {(showLotDropdown || ruleToEdit?.appliesTo == "specific-lots") && <Grid item xs={5}>
                                    <div className={classes.lotMultiSelect} >
                                        <DropdownFilter
                                            title={'Lots'}
                                            identifier={"id"}
                                            values={clientLots}
                                            onSelect={(val) => onSelectLots(val)}
                                            value={!!ruleToEdit?.lotId?.length ? [...ruleToEdit?.lotId] : []}
                                            multiple
                                        />
                                    </div>
                                </Grid>} 
                                </Authorize>
                            }
                            <Grid container>
                            <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                <CustomInput
                                    label="Activity"
                                    elementType="dropdown"
                                    value={ruleToEdit?.activityId}
                                    values={filteredActivityItems}
                                    onChange={val => onActivityChange(val)}
                                    showEmpty
                                />
                            </Authorize>
                            </Grid>
                        </Grid>
                    }                   
                </Grid>

                {ruleToEdit?.activityId && <>
                    <Grid container spcaing={2}>
                        <Grid item xs={4}>
                            <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={ruleToEdit?.calcOnCheckin}
                                                onChange={(e) => onFieldChange('calcOnCheckin', e.target.checked)}
                                                inputProps={{ 'aria-label': 'controlled' }}
                                            />
                                        }
                                        label={
                                            <div className={classes.label}>Consider Check In</div>
                                        }
                                        className={classes.wrapper}
                                    />
                                </Grid>
                                {(ruleToEdit?.calcOnCheckin === true) &&
                                    <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={ruleToEdit?.calcOnFirstCheckin}
                                                    onChange={(e) => onFieldChange('calcOnFirstCheckin', e.target.checked)}
                                                    inputProps={{ 'aria-label': 'controlled' }}
                                                />
                                            }
                                            label={
                                                <div className={classes.label}>Consider on First Check In</div>
                                            }
                                            className={classes.wrapper}
                                        />
                                    </Grid>
                                }
                            </Authorize>
                        </Grid>
                        <Grid item xs={8}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>                            
                                    <Grid item xs={4}>
                                        <CustomInput
                                            id="preferredServiceProviderId"
                                            label={"Preferred Service Provider"}
                                            value={ruleToEdit?.preferredServiceProviderId}
                                            onChange={(val) => onFieldChange('preferredServiceProviderId', parseInt(val))}
                                            elementType="dropdown"
                                            values={serviceProviders}
                                            showEmpty={true}
                                        />
                                    </Grid>                            
                                    <Grid item>
                                        <CustomInput
                                            label={"Days to Complete By"}
                                            elementType="input"
                                            value={ruleToEdit?.daysToComplete}
                                            onChange={(e) => onFieldChange('daysToComplete', e)}
                                        />
                                    </Grid>
                                </Authorize>
                            </Grid>
                        </Grid>
                    </Grid>
                </>}

                {ruleToEdit?.activityId && vehicleTypeConfigLookup?.[ruleToEdit?.activityId] &&
                    <Grid item>
                        {!!vehicleTypes?.length &&
                            <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                <DropdownFilter
                                    title="Vehicle Type"
                                    identifier={"id"}
                                    values={[{ name: 'All vehicle types', id: null }, ...vehicleTypes]}
                                    showEmpty
                                    value={ruleToEdit?.vehicleTypeIdsList ?? (ruleToEdit?.isNew ? [] : [null])}
                                    onSelect={val => onChangeVehicleTypes(val)}
                                    multiple
                                />
                            </Authorize>
                        }
                    </Grid>
                }
                <Grid item className={classes.params}>
                    <Grid container spacing={1}>
                        {allRuleConfigsLookup?.[ruleToEdit?.activityId]?.map((param, index) =>
                            <>
                                <Grid item key={index} >
                                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                        {ruleFieldTypeConfig[param.paramType]?.component(
                                            ruleToEdit?.ruleParams?.find(p => p?.paramType === param?.paramType && p?.paramSubType === param?.paramSubType),
                                            (val) => onRuleParamChange(val, param?.paramType, param?.paramSubType),
                                            param?.paramSubType
                                        )}
                                    </Authorize>
                                </Grid>
                                {index !== (allRuleConfigsLookup?.[ruleToEdit?.activityId]?.length - 1) &&
                                    <Grid item key={index} className={classes.ruleOperand}>
                                        <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                            <CustomInput
                                                // label={}
                                                elementType="dropdown"
                                                value={ruleToEdit?.ruleOperand}
                                                values={ruleOperands}
                                                onChange={val => onFieldChange('ruleOperand', parseInt(val))}
                                            />
                                        </Authorize>
                                    </Grid>
                                }
                            </>
                        )}
                    </Grid>
                </Grid>                
                {(ruleToEdit?.lotId == null && !selectLot) ? 
                <Grid item xs={6} direction='row' style={{ display: 'flex', alignItems: 'center' }}>
                        <InfoOutlinedIcon/>
                        {ruleToEdit?.allPmfLots == true ? <div className={classes.text}>This rule applies to all PMF lots</div> 
                        : ruleToEdit?.allLots == true ? <div>This rule applies to all lots</div> : null}
                </Grid> : null}
                <Grid item className={classes.btnContainer}>
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.SAVE_CONFIG}>   
                        <Button
                            onClick={cancel}
                            className={classes.button}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        {!ruleToEdit?.isNew && <Button
                            onClick={onDelete}
                            className={classes.button}
                            variant="contained"
                            color="primary"
                            disabled={isDeleting}
                        >
                            {(isDeleting) ? "Deleting..." : "Delete"}
                        </Button>}
                        <Button
                            onClick={onSave}
                            className={classes.button}
                            variant="contained"
                            color="primary"
                            disabled={isAdding || isSaving || (!ruleToEdit?.activityId ||  (selectLot && ruleToEdit?.appliesTo === "specific-lots" && !(!!ruleToEdit?.lotId?.length)))}
                        >
                            {(isAdding || isSaving) ? "Saving..." : "Save"}
                        </Button>
                    </Authorize>
                </Grid>
            </Grid>
        </Paper>
    )
}

export default Rule;